import React, { createContext, useState, useEffect, useContext } from 'react';
import { getMessageTemplates } from '../services/templatesApi';
import defaultTemplatesConfig from '../config/messageTemplatesConfig';
import { initializeTemplates, validateTemplates } from '../utils/templateUtils';
import { useAuth } from './AuthContext';

// Create context
const TemplatesContext = createContext();

// Create provider component
export const TemplatesProvider = ({ children }) => {
  const [templates, setTemplates] = useState(defaultTemplatesConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  // Use the imported default templates
  const defaultTemplates = defaultTemplatesConfig;

  // Initialize and fetch templates when component mounts or user changes
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        // Initialize templates ensures we have valid data
        const data = await initializeTemplates();
        
        if (validateTemplates(data)) {
          // console.log('Templates loaded and validated');
          
          // If we got company data from the API, use it
          if (data.companyName && currentUser?.company) {
            // console.log(`Using templates for company: ${data.companyName}`);
          } else {
            console.log('Using default templates (no company-specific data found)');
          }
          
          setTemplates(data);
          setError(null);
        } else {
          console.warn('Templates failed validation, using defaults');
          setTemplates(defaultTemplates);
        }
      } catch (err) {
        console.error('Failed to load templates:', err);
        setError('無法取得訊息模板，使用預設值');
        // Use default templates if fetch fails
        setTemplates(defaultTemplates);
      } finally {
        setLoading(false);
      }
    };

    // Reload templates when the user changes (different company)
    if (currentUser) {
      loadTemplates();
    }
  }, [currentUser?.company]);

  // Format company info HTML
  const getCompanyInfoHtml = () => {
    const t = templates || defaultTemplates;
    return `
      <p><strong>公司名稱：</strong>${t.companyName}</p>
      <p><strong>公司信箱：</strong>${t.companyEmail}</p>
      <p><strong>公司地址：</strong>${t.companyAddress}</p>
      <p><strong>統一編號：</strong>${t.companyTaxId}</p>
      <p><strong>專案聯絡人：</strong>${t.contactPerson}</p>
      <p><strong>Line客服：</strong><a href="${t.lineService}" target="_blank">${t.lineService}</a></p>
      ${t.portfolioUrl ? `<p><strong>公司作品集：</strong><a href="${t.portfolioUrl}" target="_blank">${t.portfolioUrl}</a></p>` : ''}
    `;
  };

  // Format bank info HTML
  const getBankInfoHtml = (paymentMethod, paymentDetails) => {
    const t = templates || defaultTemplates;
    return `
      <p><strong>付款方式：</strong>${
        paymentMethod === 'bank_transfer' ? '銀行轉帳' :
        paymentMethod === 'credit_card' ? '信用卡' :
        paymentMethod === 'cash' ? '現金' : '其他'
      }</p>
      <p><strong>戶名：</strong>${t.accountName || ''}</p>
      <p><strong>銀行名稱：</strong>${t.bankName || ''}</p>
      <p><strong>分行名稱：</strong>${t.branchName || ''}</p>
      <p><strong>銀行代碼：</strong>${t.bankCode || ''}</p>
      <p><strong>分行代碼：</strong>${t.branchCode || ''}</p>
      <p><strong>銀行帳號：</strong>${t.bankAccount || ''}</p>
      ${paymentDetails ? `<p><strong>付款說明：</strong>${paymentDetails}</p>` : ''}
    `;
  };

  // Format status for display
  const getStatusDisplay = (status) => {
    const t = templates || defaultTemplates;
    switch (status) {
      case 'pending':
        return t.statusPending;
      case 'partial':
        return t.statusPartial;
      case 'paid':
        return t.statusPaid;
      case 'overdue':
        return t.statusOverdue;
      default:
        return status;
    }
  };

  return (
    <TemplatesContext.Provider value={{ 
      templates: templates || defaultTemplates, 
      loading, 
      error,
      getCompanyInfoHtml,
      getBankInfoHtml,
      getStatusDisplay
    }}>
      {children}
    </TemplatesContext.Provider>
  );
};

// Custom hook to use the templates context
export const useTemplates = () => {
  const context = useContext(TemplatesContext);
  if (context === undefined) {
    throw new Error('useTemplates must be used within a TemplatesProvider');
  }
  return context;
};

export default TemplatesContext;
